import * as React from "react";
import {
  Box,
  Grid,
  Typography,
  Modal,
  Backdrop,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Stack,
} from "@mui/material";
import { useState, useEffect } from "react";
import { get, postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import Loader from "./loading-screen/Loader";
import AEPSMERCHANTBIOMETRICKYCIPAY from "../modals/aeps/AEPSMERCHANTBIOMETRICKYCIPAY";
import AuthContext from "../store/AuthContext";
import useCommonContext from "../store/CommonContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "80%", md: "60%", lg: "40%" },
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 4,
  borderRadius: "12px",
  overflowY: "auto",
  maxHeight: "90vh",
};

const Aeps1OutletStatusIpay = ({
  open,
  onClose,
  refreshCallback,
  showLoader = false,
  statusType,
}) => {
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [refrenceKey, setRefrenceKey] = useState("");
  const [pidWadth, setPidWadth] = useState("");
  const [biometricData, setBiometricData] = useState({
    aadhaar: "",
    fingerData: null,
  });
  const { user, location } = React.useContext(AuthContext);
  const { refreshUser } = useCommonContext();

  // ✅ Automatically check status when modal opens
  useEffect(() => {
    if (open) {
      checkOutletStatus();
    }
  }, [open]);

  // Reset all state when main modal closes
  const resetAllState = () => {
    setLoading(false);
    setStatusData(null);
    setError(null);
    setShowBiometricModal(false);
    setBiometricData({
      aadhaar: "",
      fingerData: null,
    });
    setRefrenceKey("");
    setPidWadth("");
  };

  const checkOutletStatus = async () => {
    setLoading(true);
    setError(null);

    postJsonData(
      ApiEndpoints.AEPS1_OUTLET_STATUS_IPAY,
      { spkey: statusType === "DMT1OUTLET" ? "DMI" : "WAP" },
      setLoading,
      (res) => {
        const responseData = res?.data?.response;

        setStatusData(responseData);

        const shouldShowBiometric =
          responseData?.status === "SUCCESS" ||
          responseData?.action === "ACTION-REQUIRED" ||
          responseData?.kyc_status === "PENDING" ||
          responseData?.biometric_required === true;

        if (shouldShowBiometric) {
          setRefrenceKey(responseData?.referenceKey || "");
          setPidWadth(responseData?.pidOptionWadh || "");

          setShowBiometricModal(true);
        } else {
          setShowBiometricModal(false);
          onClose();
        }

        // ✅ Call refreshCallback if provided
        if (refreshCallback && responseData) {
          refreshCallback(responseData);
        }
        setLoading(false);
        refreshUser();
      },
      (error) => {
        apiErrorToast(error);
        setError("Failed to check outlet status. Please try again.");
        setLoading(false);
      },
    );
  };

  const handleRetry = () => {
    setError(null);
    checkOutletStatus();
  };

  const handleBiometricSubmit = async (
    scanData = biometricData?.fingerData,
    aadhaar,
  ) => {
    setLoading(true);

    postJsonData(
      ApiEndpoints.AEPS1_MERCHANT_BIOMETRIC_KYC_IPAY,
      {
        AadharNumber: biometricData.aadhaar,
        errInfo: scanData.errInfo,
        pidDataType: scanData.type,
        pidData: scanData.pidData,
        ci: scanData.cI,
        dc: scanData.dC,
        dpId: scanData.dpId,
        fCount: scanData.fCount,
        hmac: scanData.hMac,
        mc: scanData.mC,
        mi: scanData.mI,
        nmPoints: scanData.nmPoints,
        qScore: scanData.qScore,
        rdsId: scanData.rdsId,
        rdsVer: scanData.rdsVer,
        sessionKey: scanData.sessionKey,
        srno: scanData.srno,
        pf: scanData.pf,
        ts: scanData.ts,
        referenceKey: refrenceKey,
        latitude: location.lat,
        longitude: location.long,
      },
      setLoading,
      (res) => {
        setShowBiometricModal(false);
        setBiometricData({
          aadhaar: "",
          fingerData: null,
        });

        checkOutletStatus();

        if (res?.data?.message) {
          okSuccessToast(res.data.message);
        }
      },
      (error) => {
        apiErrorToast(error);
        setError("Biometric KYC submission failed. Please try again.");
        setLoading(false);
      },
    );
  };

  // Handle biometric modal close
  const handleBiometricClose = (isCancel = false) => {
    setShowBiometricModal(false);
    if (isCancel) {
      setBiometricData({
        aadhaar: "",
        fingerData: null,
      });
    }
  };

  // Handle main modal close
  const handleClose = () => {
    resetAllState();
    if (onClose) {
      onClose(statusData);
    }
  };

  // Determine status color and message
  const getStatusDetails = () => {
    if (!statusData?.status)
      return { color: "info", message: "Checking status..." };

    switch (statusData.status) {
      case "SUCCESS":
        if (statusData.action === "ACTION-REQUIRED") {
          return {
            color: "warning",
            message:
              statusData.message ||
              "Outlet created successfully! Biometric KYC required.",
          };
        }
        return {
          color: "success",
          message:
            statusData.message || "Outlet registration completed successfully!",
        };
      case "COMPLETED":
      case "APPROVED":
        return {
          color: "success",
          message:
            statusData.message || "Outlet registration completed successfully!",
        };
      case "PENDING":
      case "IN_PROGRESS":
      case "PROCESSING":
        return {
          color: "info",
          message:
            statusData.message ||
            "Registration is being processed. Please wait...",
        };
      case "FAILED":
      case "REJECTED":
      case "ERROR":
        return {
          color: "error",
          message:
            statusData.message ||
            "Outlet registration failed. Please try again.",
        };
      default:
        return {
          color: "warning",
          message: statusData.message || "Status: " + statusData.status,
        };
    }
  };

  const statusDetails = getStatusDetails();

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="aeps-outlet-status-modal"
        aria-describedby="aeps-outlet-status-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box sx={style}>
          <Loader loading={showLoader || loading} />

          <Typography
            id="aeps-outlet-status-modal"
            variant="h6"
            component="h2"
            sx={{
              mb: 3,
              fontFamily: "Poppins",
              fontWeight: 600,
              textAlign: "center",
              color: "#1976d2",
            }}
          >
            Outlet Registration Status
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetry}
                  sx={{ fontWeight: 600 }}
                >
                  RETRY
                </Button>
              }
            >
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          {statusData && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={statusDetails.color}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  alignItems: "center",
                  py: 2,
                }}
              >
                <AlertTitle
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {statusDetails.message}
                </AlertTitle>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  {statusData.outletId && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Outlet ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {statusData.outletId}
                      </Typography>
                    </Box>
                  )}

                  {statusData.kyc_status && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        KYC Status:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {statusData.kyc_status}
                      </Typography>
                    </Box>
                  )}

                  {statusData.action && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Action Required:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            statusData.action === "ACTION-REQUIRED"
                              ? "error.main"
                              : "success.main",
                        }}
                      >
                        {statusData.action}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Alert>

              {statusData.status === "SUCCESS" &&
                statusData.action === "ACTION-REQUIRED" && (
                  <Box sx={{ textAlign: "center", mt: 3 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setShowBiometricModal(true)}
                      sx={{
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #1976d2 0%, #2196f3 100%)",
                      }}
                    >
                      Proceed with Biometric KYC
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "text.secondary",
                      }}
                    >
                      Click to complete biometric verification
                    </Typography>
                  </Box>
                )}
            </Box>
          )}

          {!statusData && !error && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Checking outlet status...
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ mr: 2, borderRadius: 2 }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={checkOutletStatus}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Refresh Status
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Biometric KYC Modal - Auto opens when condition is met */}
      {showBiometricModal && (
        <Modal
          open={showBiometricModal}
          onClose={() => handleBiometricClose(true)}
          aria-labelledby="biometric-kyc-modal"
          closeAfterTransition
          BackdropComponent={Backdrop}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", sm: "80%", md: "70%", lg: "50%" },
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
            }}
          >
            <AEPSMERCHANTBIOMETRICKYCIPAY
              open={showBiometricModal}
              onClose={() => handleBiometricClose(true)}
              title="Biometric KYC Verification"
              pidWadth={pidWadth}
              formData={{}}
              setFormData={() => {}}
              banks={[]}
              buttons={[]}
              fingerData={(data) =>
                setBiometricData((prev) => ({ ...prev, fingerData: data }))
              }
              aadhaar={biometricData.aadhaar}
              setAadhaar={(value) =>
                setBiometricData((prev) => ({ ...prev, aadhaar: value }))
              }
              onFingerSuccess={handleBiometricSubmit}
            />
          </Box>
        </Modal>
      )}
    </>
  );
};

export default Aeps1OutletStatusIpay;
